import { Title, ContentItem, ResourceType } from "@/types/content"
import { ref, watch } from 'vue'
import { useHead } from '@unhead/vue'
import { getConfig } from '@/config'
import { ImageService } from '@/utils/image'
import type { ImageData } from '@/types/content'
import { getDisplayTitle } from '@/utils/helpers'
import { resolveAbsoluteUrl } from '@/utils/url'
import { resolveOgImagePath } from '@/utils/og-image'

// Extended SEO options interface with more metadata fields
export interface SEOOptions {
  title?: string
  description?: string
  image?: string | ImageData
  webpImage?: string | ImageData
  ogImage?: string
  twitterImage?: string
  type?: 'website' | 'article' | 'book' | 'profile' | string
  url?: string
  canonical?: string
  siteName?: string
  author?: string
  publishedTime?: string
  modifiedTime?: string
  keywords?: string[]
  language?: string
  alternateUrls?: { [lang: string]: string }
  section?: string
  tags?: string[]
  locale?: string
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player'
  twitterCreator?: string
}

export function useSEO() {
  const config = getConfig()
  
  // Keep track of current metadata for access across components
  const currentMeta = ref<SEOOptions>({
    title: config.app.name,
    description: config.seo.description,
    image: config.seo.ogImage,
    webpImage: config.seo.webpImage,
    type: 'website',
    url: window.location.href,
    siteName: config.app.name,
    twitterCard: 'summary_large_image',
    locale: 'en_US',
    language: 'en'
  })

  /**
   * Update all metadata tags
   */
  const updateMeta = (options: SEOOptions) => {
    // NOTE: This must come before any `config` usage in this function.
    // There is also a `config` in the outer `useSEO()` scope; keeping this first
    // prevents temporal-dead-zone issues if the inner binding shadows it.
    const config = getConfig()

    const {
      title,
      description,
      image,
      webpImage,
      ogImage,
      twitterImage,
      type,
      url,
      canonical,
      siteName,
      author,
      publishedTime,
      modifiedTime,
      keywords,
      language,
      alternateUrls,
      section,
      tags,
      locale,
      twitterCard,
      twitterCreator
    } = options

    // Prefer raster originals (PNG/JPG) for social crawlers — not WebP src
    const rawImageUrl =
      ImageService.getSocialShareUrl(image, { og_image: ogImage, twitter_image: twitterImage }, config.seo.ogImage) ||
      undefined
    const imageUrl = rawImageUrl
      ? resolveAbsoluteUrl(resolveOgImagePath(rawImageUrl, config.app.id))
      : undefined
    const webpImageUrl = typeof webpImage === 'string' ? webpImage : ImageService.getImageUrl(webpImage) || undefined

    // Update current meta state
    if (title) currentMeta.value.title = title
    if (description) currentMeta.value.description = description
    if (imageUrl) currentMeta.value.image = imageUrl
    if (webpImageUrl) currentMeta.value.webpImage = webpImageUrl
    if (type) currentMeta.value.type = type
    if (url) currentMeta.value.url = url
    if (canonical) currentMeta.value.canonical = canonical
    if (siteName) currentMeta.value.siteName = siteName
    if (author) currentMeta.value.author = author
    if (publishedTime) currentMeta.value.publishedTime = publishedTime
    if (modifiedTime) currentMeta.value.modifiedTime = modifiedTime
    if (keywords) currentMeta.value.keywords = keywords
    if (language) currentMeta.value.language = language
    if (alternateUrls) currentMeta.value.alternateUrls = alternateUrls
    if (section) currentMeta.value.section = section
    if (tags) currentMeta.value.tags = tags
    
    // Update document title
    if (title) {
      document.title = `${title} | ${config.app.name}`
    }
    
    // Basic meta tags
    const basicMetaTags: Record<string, string | undefined> = {
      description,
      keywords: keywords?.join(', ')
    }
    
    // Open Graph meta tags
    const ogMetaTags: Record<string, string | undefined> = {
      'og:title': title,
      'og:description': description,
      'og:image': imageUrl,
      'og:type': type || 'website',
      'og:url': url,
      'og:site_name': siteName || config.app.name,
      'og:locale': locale || 'en_US',
      'article:author': author,
      'article:published_time': publishedTime,
      'article:modified_time': modifiedTime
    }
    
    // Twitter Card meta tags
    const twitterMetaTags: Record<string, string | undefined> = {
      'twitter:card': twitterCard || 'summary_large_image',
      'twitter:title': title,
      'twitter:description': description,
      'twitter:image': imageUrl,
      'twitter:creator': twitterCreator
    }

    // Update basic meta tags
    updateMetaTags(basicMetaTags, false)
    
    // Update Open Graph meta tags
    updateMetaTags(ogMetaTags, true)
    
    // Update Twitter Card meta tags
    updateMetaTags(twitterMetaTags, false)
    
    // Update canonical URL
    updateCanonicalUrl(canonical || url)
    
    // Update language
    if (language) {
      document.documentElement.setAttribute('lang', language)
    }
    
    // Update alternate language URLs
    updateAlternateUrls(alternateUrls)
  }
  
  /**
   * Helper function to update meta tags
   */
  const updateMetaTags = (tags: Record<string, string | undefined>, isOpenGraph: boolean) => {
    Object.entries(tags).forEach(([name, content]) => {
      if (content) {
        const selector = isOpenGraph
          ? `meta[property="${name}"]`
          : `meta[name="${name}"]`
        let element = document.querySelector(selector)
        
        if (element) {
          element.setAttribute('content', content)
        } else {
          // Create new meta tag if it doesn't exist
          element = document.createElement('meta')
          element.setAttribute(isOpenGraph ? 'property' : 'name', name)
          element.setAttribute('content', content)
          document.head.appendChild(element)
        }
      }
    })
  }
  
  /**
   * Update canonical URL
   */
  const updateCanonicalUrl = (url?: string) => {
    if (!url) return
    
    let canonicalElement = document.querySelector('link[rel="canonical"]')
    
    if (canonicalElement) {
      canonicalElement.setAttribute('href', url)
    } else {
      canonicalElement = document.createElement('link')
      canonicalElement.setAttribute('rel', 'canonical')
      canonicalElement.setAttribute('href', url)
      document.head.appendChild(canonicalElement)
    }
  }
  
  /**
   * Update alternate language URLs
   */
  const updateAlternateUrls = (alternateUrls?: { [lang: string]: string }) => {
    if (!alternateUrls) return
    
    // Remove existing alternate language links
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => {
      el.remove()
    })
    
    // Add new alternate language links
    Object.entries(alternateUrls).forEach(([lang, href]) => {
      const alternateElement = document.createElement('link')
      alternateElement.setAttribute('rel', 'alternate')
      alternateElement.setAttribute('hreflang', lang)
      alternateElement.setAttribute('href', href)
      document.head.appendChild(alternateElement)
    })
  }

  /**
   * Update metadata for a Title
   */
  const updateMetaWithTitle = (title: Title) => {
    const config = getConfig()
    const url = window.location.origin + `/titles/${title.id}/${title.slug || ''}`
    const seo = title.meta?.seo

    updateMeta({
      title: seo?.title ?? getDisplayTitle(title),
      description: seo?.description ?? title.description,
      image: title.image,
      ogImage: seo?.og_image,
      twitterImage: seo?.twitter_image,
      type: seo?.type ?? 'article',
      url: url,
      canonical: url,
      siteName: config.app.name,
      publishedTime: title.publishedAt,
      author: title.authors?.map(a => a.name).join(', '),
      twitterCard: 'summary_large_image'
    })
  }

  // Get default metadata for a specific resource type
  const getResourceDefaultMeta = (resourceType: ResourceType): SEOOptions => {
    const config = getConfig()
    const defaults = config.seo.defaults
    
    // Type guard to ensure resourceType is a valid key
    const validResourceType = (resourceType as string) in (defaults || {}) 
      ? resourceType 
      : null

    if (validResourceType && defaults && defaults[validResourceType as keyof typeof defaults]) {
      const resourceDefaults = defaults[validResourceType as keyof typeof defaults]
      return {
        title: resourceDefaults?.title || `${resourceType.charAt(0).toUpperCase() + resourceType.slice(1)} | ${config.app.name}`,
        description: resourceDefaults?.description || config.seo.description,
        image: config.seo.ogImage,
        webpImage: config.seo.webpImage,
        type: resourceDefaults?.type || 'website',
        url: window.location.href,
        canonical: window.location.href,
        siteName: config.app.name,
        twitterCard: 'summary_large_image'
      }
    }
    
    // Fallback defaults if no specific resource defaults exist
    return {
      title: `${String(resourceType).charAt(0).toUpperCase() + String(resourceType).slice(1)} | ${config.app.name}`,
      description: config.seo.description,
      image: config.seo.ogImage,
      webpImage: config.seo.webpImage,
      type: 'website',
      url: window.location.href,
      canonical: window.location.href,
      siteName: config.app.name,
      twitterCard: 'summary_large_image'
    }
  }

  // Improved method to extract and apply meta from API responses
  const updateMetaFromApiResponse = (
    apiMeta?: {
      title?: string
      description?: string
      image?: string | ImageData
      webpImage?: string | ImageData
      og_image?: string
      twitter_image?: string
      type?: string
    },
    item?: any,
    defaultTitle?: string,
    resourceType?: ResourceType,
    options?: {
      forceUpdate?: boolean; // When true, will override existing metadata
      isPrimary?: boolean;   // When true, marks this as primary content (like a header item)
    }
  ) => {
    const config = getConfig()
    
    // Generate canonical URL
    let canonical = window.location.href
    if (item && item.type && item.id) {
      const origin = window.location.origin
      if (item.type === 'titles' && item.slug) {
        canonical = `${origin}/titles/${item.id}/${item.slug}`
      } else if (item.slug) {
        canonical = `${origin}/${item.type}/${item.slug}`
      } else {
        canonical = `${origin}/${item.type}/${item.id}`
      }
    }

    // Get author information
    let author = ''
    if (item?.authors && Array.isArray(item.authors) && item.authors.length > 0) {
      author = item.authors.map((a: any) => a.name || a.title).join(', ')
    }

    // Prioritize metadata sources: API meta > item meta > item fields > defaults
    const metaOptions: SEOOptions = {
      // Title priority: API meta > item meta > display title (with override) > provided default > resource default > app name
      title: apiMeta?.title || 
             item?.meta?.seo?.title || 
             (item ? getDisplayTitle(item) : undefined) || 
             defaultTitle || 
             (resourceType && typeof resourceType === 'string'
               ? getResourceDefaultMeta(resourceType).title
               : getConfig().app.name),
             
      // Description priority: API meta > item meta > item description > resource default > app default
      description: apiMeta?.description || 
                  item?.meta?.seo?.description || 
                  item?.description || 
                  (resourceType ? getResourceDefaultMeta(resourceType).description : getConfig().seo.description),
                  
      // Image: API meta.seo og_image (raster) > structured image original > app default
      image: item?.image,
      ogImage: apiMeta?.og_image ?? item?.meta?.seo?.og_image,
      twitterImage: apiMeta?.twitter_image ?? item?.meta?.seo?.twitter_image,
                 
      // Type priority: API meta > item meta > based on item type > resource default > website
      type: apiMeta?.type || 
            item?.meta?.seo?.type || 
            (item?.type === 'titles' ? 'article' : 'website') ||
            (resourceType ? getResourceDefaultMeta(resourceType).type : 'website'),
            
      url: window.location.href,
      canonical: canonical,
      siteName: getConfig().app.name,
      author: author,
      publishedTime: item && typeof item === 'object' && 'publishedAt' in item && typeof item.publishedAt === 'string' 
        ? item.publishedAt 
        : (item && typeof item === 'object' && 'createdAt' in item && typeof item.createdAt === 'string' 
            ? item.createdAt 
            : undefined),
      modifiedTime: item?.updatedAt,
      section: item?.categories?.[0]?.name,
      tags: [
        ...(item?.categories?.map((cat: any) => cat.name) || []),
        ...(item?.tags?.map((tag: any) => tag.name) || [])
      ]
    }

    updateMeta(metaOptions)
  }

  // Reset to default metadata
  const resetMeta = () => {
    const config = getConfig()
    updateMeta({
      title: config.app.name,
      description: config.seo.description,
      image: config.seo.ogImage,
      webpImage: config.seo.webpImage,
      type: 'website',
      url: window.location.href,
      canonical: window.location.origin,
      siteName: config.app.name,
      twitterCard: 'summary_large_image'
    })
  }

  return {
    updateMeta,
    updateMetaWithTitle,
    updateMetaFromApiResponse,
    resetMeta,
    currentMeta,
    getResourceDefaultMeta
  }
} 